import React, { useState } from 'react';
import { useNotif } from '../context/NotifContext';
import { useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, IconButton, Button,
    Avatar, Chip, Divider, LinearProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    VisibilityOutlined, VisibilityOffOutlined,
    ArrowUpward, ArrowDownward,
    PhoneIphoneOutlined, WifiOutlined, FlashOnOutlined,
    AccountBalanceOutlined, ShareOutlined,
    NotificationsOutlined, SwapHorizOutlined, QrCodeOutlined,
    ContentCopyOutlined, CheckCircleOutline,
    TrendingUpOutlined, CreditCardOutlined, ReceiptOutlined,
    SupportAgentOutlined, LockOutlined, LocalAtmOutlined,
    CloseOutlined, NorthEastOutlined, SouthWestOutlined,
} from '@mui/icons-material';


const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    cream:   '#FAF9FC',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    redDim:  '#FF4D6A18',
};

const SERVICES = [
    { label: 'Add Money',    icon: <ArrowUpward />,            path: '/fund',      color: B.green   },
    { label: 'Send',         icon: <SwapHorizOutlined />,      path: '/transfer',  color: B.purple  },
    { label: 'Withdraw',     icon: <ArrowDownward />,          path: '/withdraw',  color: B.red     },
    { label: 'Airtime',      icon: <PhoneIphoneOutlined />,    path: '/airtime',   color: '#FF9500' },
    { label: 'Data',         icon: <WifiOutlined />,           path: '/datapage',  color: '#007AFF' },
    { label: 'Bills',        icon: <FlashOnOutlined />,        path: '/bills',     color: '#FF6B35' },
    { label: 'Link Bank',    icon: <AccountBalanceOutlined />, path: '/link-bank', color: '#34C759' },
    { label: 'Credit Score', icon: <TrendingUpOutlined />,     path: '/dashboard', color: B.light   },
    { label: 'Card',         icon: <CreditCardOutlined />,     path: '/card',      color: '#5856D6' },
    { label: 'Loans',        icon: <LocalAtmOutlined />,       path: '/loans',     color: '#8B6914' },
    { label: 'Refer & Earn', icon: <ShareOutlined />,          path: '/refer',     color: '#FF2D55' },
    { label: 'Support',      icon: <SupportAgentOutlined />,   path: '/support',   color: '#636366' },
];

const TXS = [
    { id: 1, type: 'credit', title: 'Salary Credit',        sub: 'Access Bank',  amount: 80000, date: 'Today · 9:14 AM',     status: 'success' },
    { id: 2, type: 'debit',  title: 'MTN Airtime',          sub: '080 *** 4521', amount: 1000,  date: 'Today · 8:02 AM',     status: 'success' },
    { id: 3, type: 'debit',  title: 'Electricity Bill',     sub: 'EKEDC',        amount: 15000, date: 'Yesterday · 6:45 PM', status: 'success' },
    { id: 4, type: 'credit', title: 'Wallet Funding',       sub: 'GTBank',       amount: 20000, date: 'Yesterday · 2:30 PM', status: 'success' },
    { id: 5, type: 'debit',  title: 'Transfer · Emeka O.',  sub: 'Zenith Bank',  amount: 5000,  date: 'Mon · 11:00 AM',      status: 'success' },
    { id: 6, type: 'debit',  title: 'Shoprite Purchase',    sub: 'Card Payment', amount: 8500,  date: 'Mon · 3:22 PM',       status: 'pending' },
];

const WalletPage: React.FC = () => {
    const [showBal,   setShowBal]   = useState(false);
    const [balance]                  = useState(125000);
    const [interest]                 = useState(500);
    const [copied,    setCopied]    = useState(false);
    const [showNudge, setShowNudge] = useState(true);
    const {hasUnread,  clearUnread}  = useNotif();
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const acct     = '7823 4561 09';
    const score    = 653;
    const scoreMax = 850;

    const fmt = (n: number) =>
        new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

    const handleCopy = () => {
        navigator.clipboard.writeText(acct.replace(/\s/g, ''));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;

    return (
        <Box sx={{ minHeight: '100vh', background: isDark ? B.deep : B.lavender, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO HEADER ══════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                pt: { xs: 9, sm: 5.5 }, pb: 14, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .2)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: 0, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.accent, .1)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                {/* top row */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4.5}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, fontFamily: "'Instrument Serif', serif", fontSize: '1rem', fontWeight: 400 }}>AB</Avatar>
                        <Box>
                            <Typography sx={{ color: alpha('#fff', .4), fontSize: '.65rem', letterSpacing: '.12em', textTransform: 'uppercase' }}>Good morning</Typography>
                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.95rem', letterSpacing: '-.02em' }}>Ahmed Bello</Typography>
                        </Box>
                    </Box>
                    <Box display="flex" gap={.75}>
                        <Box sx={{ position: 'relative' }}>
                            <IconButton size="small" onClick={() => { navigate('/notifications'); clearUnread(); }} sx={{
                                color: alpha('#fff', .6), width: 36, height: 36,
                                background: alpha('#fff', .08), border: `1px solid ${alpha('#fff', .1)}`,
                                backdropFilter: 'blur(8px)',
                                '&:hover': { background: alpha('#fff', .16) },
                            }}>
                                <NotificationsOutlined sx={{ fontSize: 17 }} />
                            </IconButton>
                            {hasUnread && (
                                <Box sx={{
                                    position: 'absolute', top: 4, right: 4,
                                    width: 8, height: 8, borderRadius: '50%',
                                    background: B.red,
                                    boxShadow: `0 0 0 1.5px ${alpha(B.deep, .9)}, 0 0 8px ${alpha(B.red, .85)}`,
                                    pointerEvents: 'none',
                                }} />
                            )}
                        </Box>
                        <IconButton size="small" onClick={() => navigate('/qr')} sx={{
                            color: alpha('#fff', .6), width: 36, height: 36,
                            background: alpha('#fff', .08), border: `1px solid ${alpha('#fff', .1)}`,
                            backdropFilter: 'blur(8px)',
                            '&:hover': { background: alpha('#fff', .16) },
                        }}>
                            <QrCodeOutlined sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* balance block */}
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ color: alpha('#fff', .35), fontSize: '.65rem', letterSpacing: '.2em', textTransform: 'uppercase', mb: 1.25 }}>
                        Total Balance
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} mb={.75}>
                        <Typography sx={{
                            color: '#fff',
                            fontFamily: "'Instrument Serif', Georgia, serif",
                            fontSize: { xs: '2.1rem', sm: '2.6rem' },
                            fontWeight: 400,
                            letterSpacing: '-.02em',
                            lineHeight: 1,
                        }}>
                            {showBal ? fmt(balance) : '₦ ·· ·· ··'}
                        </Typography>
                        <IconButton onClick={() => setShowBal(!showBal)} sx={{ color: alpha('#fff', .35), p: .5, '&:hover': { color: '#fff' }, transition: 'color .2s' }}>
                            {showBal ? <VisibilityOutlined sx={{ fontSize: 16 }} /> : <VisibilityOffOutlined sx={{ fontSize: 16 }} />}
                        </IconButton>
                    </Box>

                    <Typography sx={{ color: B.green, fontSize: '.76rem', fontWeight: 600, letterSpacing: '.01em', mb: 2.5 }}>
                        {showBal ? `＋ ${fmt(interest)} interest this month` : '＋ ── interest this month'}
                    </Typography>

                    {/* account number pill */}
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .75, background: alpha('#fff', .07), border: `1px solid ${alpha('#fff', .1)}`, borderRadius: 20, px: 2, py: .7, backdropFilter: 'blur(12px)' }}>
                        <Typography sx={{ color: alpha('#fff', .45), fontSize: '.68rem', letterSpacing: '.06em' }}>
                            {acct}
                        </Typography>
                        <IconButton onClick={handleCopy} sx={{ p: 0, color: alpha('#fff', .35), '&:hover': { color: '#fff' }, transition: 'color .2s' }}>
                            {copied
                                ? <CheckCircleOutline sx={{ fontSize: 13, color: B.green }} />
                                : <ContentCopyOutlined sx={{ fontSize: 13 }} />
                            }
                        </IconButton>
                    </Box>
                </Box>

                {/* 3 primary CTAs */}
                <Box display="flex" justifyContent="center" gap={4} mt={4}>
                    {[
                        { label: 'Add',      Icon: NorthEastOutlined, color: B.green,  path: '/fund'     },
                        { label: 'Send',     Icon: SwapHorizOutlined, color: '#fff',   path: '/transfer' },
                        { label: 'Withdraw', Icon: SouthWestOutlined, color: B.red,    path: '/withdraw' },
                    ].map(({ label, Icon, color, path }) => (
                        <Box key={label} onClick={() => navigate(path)} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: .75, cursor: 'pointer' }}>
                            <Box sx={{
                                width: 52, height: 52, borderRadius: '50%',
                                background: alpha('#fff', .09),
                                border: `1.5px solid ${alpha('#fff', .16)}`,
                                backdropFilter: 'blur(12px)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color,
                                transition: 'all .25s',
                                '&:hover': { background: alpha('#fff', .18), transform: 'translateY(-3px)', boxShadow: `0 10px 28px ${alpha(color, .35)}` },
                            }}>
                                <Icon sx={{ fontSize: 21 }} />
                            </Box>
                            <Typography sx={{ color: alpha('#fff', .55), fontSize: '.68rem', fontWeight: 600, letterSpacing: '.03em' }}>{label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ══ CARDS FLOAT UP OVER HEADER ═══════════════════════════ */}
            <Container maxWidth="sm" sx={{ mt: -7, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
                {/* ── Services grid ── */}
                <Box sx={{ mb: 2, borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, backdropFilter: 'blur(20px)', p: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                        <Typography sx={{ fontWeight: 700, fontSize: '.72rem', letterSpacing: '.12em', textTransform: 'uppercase', color: alpha(isDark ? '#fff' : B.ink, .3) }}>
                            Services
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.25 }}>
                        {SERVICES.map((s, i) => (
                            <Box key={i} onClick={() => navigate(s.path)} sx={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: .6,
                                cursor: 'pointer', py: 1.25, borderRadius: 2.5,
                                transition: 'all .2s',
                                '&:hover': { background: alpha(s.color, .07), transform: 'translateY(-2px)' },
                            }}>
                                <Box sx={{
                                    width: 48, height: 48, borderRadius: 2.5,
                                    background: alpha(s.color, .1),
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: s.color, '& svg': { fontSize: 22 },
                                }}>
                                    {s.icon}
                                </Box>
                                <Typography sx={{ fontSize: '.68rem', fontWeight: 600, textAlign: 'center', color: alpha(isDark ? '#fff' : B.ink, .55), lineHeight: 1.2 }}>
                                    {s.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ── Loan + Card ── */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    <Box onClick={() => navigate('/loans')} sx={{
                        borderRadius: 3, p: 2.5, cursor: 'pointer', overflow: 'hidden', position: 'relative',
                        background: `linear-gradient(145deg, ${B.dark} 0%, ${B.mid} 100%)`,
                        transition: 'transform .25s, box-shadow .25s',
                        '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 16px 40px ${alpha(B.dark, .45)}` },
                    }}>
                        <Box sx={{ position: 'absolute', top: -25, right: -25, width: 100, height: 100, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                        <LocalAtmOutlined sx={{ color: alpha('#fff', .45), fontSize: 22, mb: 1.5 }} />
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.85rem', letterSpacing: '-.02em', mb: .4 }}>Instant Loan</Typography>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.67rem', mb: 1.75, lineHeight: 1.4 }}>Up to ₦5,000,000</Typography>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .5, background: alpha(B.green, .15), border: `1px solid ${alpha(B.green, .3)}`, borderRadius: 8, px: 1, py: .4 }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: B.green, boxShadow: `0 0 6px ${B.green}` }} />
                            <Typography sx={{ color: B.green, fontSize: '.58rem', fontWeight: 700, letterSpacing: '.06em' }}>ELIGIBLE</Typography>
                        </Box>
                    </Box>

                    <Box onClick={() => navigate('/card')} sx={{
                        borderRadius: 3, p: 2.5, cursor: 'pointer', overflow: 'hidden', position: 'relative',
                        background: 'linear-gradient(145deg, #0F0C29 0%, #302B63 60%, #24243e 100%)',
                        transition: 'transform .25s, box-shadow .25s',
                        '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 16px 40px ${alpha('#0F0C29', .55)}` },
                    }}>
                        <Box sx={{ position: 'absolute', top: -25, right: -25, width: 100, height: 100, borderRadius: '50%', background: `radial-gradient(circle, ${alpha('#8B6FFF', .22)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                        <CreditCardOutlined sx={{ color: alpha('#fff', .45), fontSize: 22, mb: 1.5 }} />
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.85rem', letterSpacing: '-.02em', mb: .4 }}>Virtual Card</Typography>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.67rem', mb: 1.75, lineHeight: 1.4 }}>Instant issuance</Typography>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .5, background: alpha('#8B6FFF', .18), border: `1px solid ${alpha('#8B6FFF', .3)}`, borderRadius: 8, px: 1, py: .4 }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: '#8B6FFF', boxShadow: '0 0 6px #8B6FFF' }} />
                            <Typography sx={{ color: '#8B6FFF', fontSize: '.58rem', fontWeight: 700, letterSpacing: '.06em' }}>GET CARD</Typography>
                        </Box>
                    </Box>
                </Box>

                {/* ── Refer & Earn ── */}
                <Box sx={{
                    mb: 2, borderRadius: 3, overflow: 'hidden',
                    background: isDark ? alpha('#FF2D55', .1) : alpha('#FF2D55', .04),
                    border: `1px solid ${alpha('#FF2D55', .14)}`,
                }}>
                    <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '.85rem', color: isDark ? '#fff' : B.ink, letterSpacing: '-.02em' }}>
                                Refer & earn <span style={{ color: '#FF2D55' }}>₦1,000</span>
                            </Typography>
                            <Typography sx={{ fontSize: '.7rem', color: alpha(isDark ? '#fff' : B.ink, .4), mt: .25 }}>
                                For every friend who signs up
                            </Typography>
                        </Box>
                        <Button onClick={() => navigate('/refer')} size="small" variant="contained"
                                sx={{ background: '#FF2D55', color: '#fff', borderRadius: 10, textTransform: 'none', fontWeight: 700, fontSize: '.72rem', px: 2, py: .9, boxShadow: 'none', '&:hover': { background: '#e0264c', boxShadow: `0 6px 18px ${alpha('#FF2D55', .4)}` } }}>
                            Invite
                        </Button>
                    </Box>
                </Box>

                {/* ── Transactions ── */}
                <Box sx={{ borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, backdropFilter: 'blur(20px)', overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '.95rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>Transactions</Typography>
                        <Button size="small" onClick={() => navigate('/history')}
                                sx={{ color: B.purple, fontWeight: 600, fontSize: '.7rem', textTransform: 'none', px: 1, borderRadius: 2, '&:hover': { background: alpha(B.purple, .07) } }}>
                            See all
                        </Button>
                    </Box>

                    {TXS.map((tx, i) => (
                        <React.Fragment key={tx.id}>
                            <Box sx={{
                                px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 2,
                                cursor: 'pointer', transition: 'background .15s',
                                '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .025) },
                            }} onClick={() => navigate(`/transaction/${tx.id}`)}>
                                <Box sx={{
                                    width: 40, height: 40, borderRadius: 2.5, flexShrink: 0,
                                    background: tx.type === 'credit' ? B.greenDim : B.redDim,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    {tx.type === 'credit'
                                        ? <SouthWestOutlined sx={{ color: B.green, fontSize: 16 }} />
                                        : <NorthEastOutlined sx={{ color: B.red,   fontSize: 16 }} />
                                    }
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography sx={{ fontWeight: 600, fontSize: '.84rem', letterSpacing: '-.015em', color: isDark ? '#fff' : B.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {tx.title}
                                        </Typography>
                                        {tx.status === 'pending' && (
                                            <Chip label="pending" size="small" sx={{ height: 16, fontSize: '.58rem', fontWeight: 700, background: alpha('#FF9500', .12), color: '#FF9500', letterSpacing: '.02em', borderRadius: 1 }} />
                                        )}
                                    </Box>
                                    <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink, .32), mt: .15 }}>
                                        {tx.sub} · {tx.date}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '.86rem', color: tx.type === 'credit' ? B.green : (isDark ? '#fff' : B.ink), flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                                    {tx.type === 'credit' ? '+' : '−'}₦{tx.amount.toLocaleString()}
                                </Typography>
                            </Box>
                            {i < TXS.length - 1 && <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />}
                        </React.Fragment>
                    ))}

                    <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${alpha(isDark ? '#fff' : B.dark, .055)}` }}>
                        <Button fullWidth onClick={() => navigate('/history')}
                                sx={{ color: alpha(isDark ? '#fff' : B.ink, .35), textTransform: 'none', fontWeight: 600, fontSize: '.76rem', py: .75, borderRadius: 2, letterSpacing: '.01em', '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .035), color: isDark ? '#fff' : B.ink } }}>
                            View full history
                        </Button>
                    </Box>
                </Box>

                {/* footer */}
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default WalletPage;